/**
 * Notification Positioning Utility
 * Calculates positions for notifications based on configuration
 */

import { NotificationPosition, NotificationConfig } from '../types';

/**
 * Default edge offsets for different positions
 */
const DEFAULT_EDGE_OFFSETS: Record<NotificationPosition, { top?: string; right?: string; bottom?: string; left?: string }> = {
  'top-right': { top: '1rem', right: '1rem' },
  'top-left': { top: '1rem', left: '1rem' },
  'top-center': { top: '1rem', left: '50%' },
  'bottom-right': { bottom: '1rem', right: '1rem' },
  'bottom-left': { bottom: '1rem', left: '1rem' },
  'bottom-center': { bottom: '1rem', left: '50%' },
};

/**
 * Calculate the base position styles for a notification container
 */
export const getContainerPositionStyles = (
  position: NotificationPosition = 'top-right',
  customOffset?: NotificationConfig['edgeOffset']
): React.CSSProperties => {
  const defaultOffset = DEFAULT_EDGE_OFFSETS[position];
  const offset = { ...defaultOffset, ...customOffset };

  const styles: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  };

  // Apply positioning
  if (offset.top !== undefined) styles.top = offset.top;
  if (offset.right !== undefined) styles.right = offset.right;
  if (offset.bottom !== undefined) styles.bottom = offset.bottom;
  if (offset.left !== undefined) styles.left = offset.left;

  // Handle centering
  if (position.includes('center')) {
    styles.transform = 'translateX(-50%)';
  }

  // Handle stack direction
  if (position.includes('bottom')) {
    styles.flexDirection = 'column-reverse';
  }

  return styles;
};

/**
 * Calculate individual notification position within the container
 */
export const getNotificationPositionStyles = (
  index: number,
  position: NotificationPosition = 'top-right',
  stackSpacing: number = 0.5,
  isEntering: boolean = false,
  isExiting: boolean = false
): React.CSSProperties => {
  const styles: React.CSSProperties = {
    pointerEvents: 'auto',
    width: 'var(--notification-width, 24rem)',
    maxWidth: 'calc(100vw - 4rem)',
  };

  // Add transform for animations
  if (isEntering) {
    // Slide in from the side
    if (position.includes('right')) {
      styles.transform = 'translateX(calc(100% + 2rem))';
    } else if (position.includes('left')) {
      styles.transform = 'translateX(calc(-100% - 2rem))';
    } else {
      styles.transform = 'translateY(-2rem)';
    }
    styles.opacity = 0;
  } else if (isExiting) {
    // Fade out
    styles.opacity = 0;
    styles.transform = 'scale(0.95)';
  }

  return styles;
};

/**
 * Get animation styles based on animation state
 */
export const getAnimationStyles = (
  animationState: string,
  position: NotificationPosition = 'top-right',
  animationDuration: number = 300
): React.CSSProperties => {
  const baseTransition = `all ${animationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;

  switch (animationState) {
    case 'entering':
      return {
        opacity: 0,
        transform: getEnterTransform(position),
        transition: baseTransition,
      };
    case 'idle':
      return {
        opacity: 1,
        transform: 'translate(0, 0) scale(1)',
        transition: baseTransition,
      };
    case 'exiting':
      return {
        opacity: 0,
        transform: 'scale(0.95)',
        transition: `opacity ${animationDuration}ms ease-out, transform ${animationDuration}ms ease-out`,
      };
    case 'exited':
      return {
        display: 'none',
      };
    default:
      return {};
  }
};

/**
 * Get enter transform based on position
 */
const getEnterTransform = (position: NotificationPosition): string => {
  if (position.includes('right')) {
    return 'translateX(calc(100% + 2rem))';
  } else if (position.includes('left')) {
    return 'translateX(calc(-100% - 2rem))';
  } else if (position.includes('top')) {
    return 'translateY(-2rem)';
  } else {
    return 'translateY(2rem)';
  }
};

/**
 * Calculate viewport-aware position
 * Adjusts position if notification would overflow viewport
 */
export const getViewportAwarePosition = (
  preferredPosition: NotificationPosition,
  notificationWidth: number = 384, // 24rem default
  notificationHeight: number = 100,
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight,
  edgeOffset: number = 16 // 1rem default
): NotificationPosition => {
  // Check if notification fits in preferred position
  const fitsHorizontally = notificationWidth + (edgeOffset * 2) <= viewportWidth;
  const fitsVertically = notificationHeight + (edgeOffset * 2) <= viewportHeight;

  if (!fitsHorizontally || !fitsVertically) {
    // On mobile, always use top-center
    if (viewportWidth < 640) {
      return 'top-center';
    }

    // Adjust position based on what doesn't fit
    if (!fitsHorizontally && preferredPosition.includes('right')) {
      return preferredPosition.replace('right', 'left') as NotificationPosition;
    }
    if (!fitsHorizontally && preferredPosition.includes('left')) {
      return preferredPosition.replace('left', 'center') as NotificationPosition;
    }
    if (!fitsVertically && preferredPosition.includes('bottom')) {
      return preferredPosition.replace('bottom', 'top') as NotificationPosition;
    }
  }

  return preferredPosition;
};

/**
 * Get responsive width for notifications
 */
export const getResponsiveWidth = (viewportWidth: number): string => {
  if (viewportWidth < 640) {
    // Mobile: full width minus padding
    return 'calc(100vw - 2rem)';
  } else if (viewportWidth < 1024) {
    // Tablet: slightly smaller
    return '20rem';
  } else {
    // Desktop: default
    return '24rem';
  }
};

/**
 * Calculate z-index for stacked notifications
 * Higher index = higher z-index (newer notifications on top)
 */
export const getStackedZIndex = (
  baseZIndex: number = 9999,
  index: number,
  total: number
): number => {
  return baseZIndex + (total - index);
};

/**
 * Get mobile-optimized styles
 */
export const getMobileStyles = (isMobile: boolean): React.CSSProperties => {
  if (!isMobile) return {};

  return {
    width: 'calc(100vw - 2rem)',
    maxWidth: 'none',
    left: '1rem',
    right: '1rem',
    transform: 'none',
  };
};

/**
 * Calculate offset for header-aware positioning
 * Accounts for fixed headers in the application
 */
export const getHeaderAwareOffset = (
  position: NotificationPosition,
  headerHeight: number = 0,
  defaultOffset: string = '2rem'
): string => {
  if (!position.includes('top')) {
    return defaultOffset;
  }

  if (headerHeight > 0) {
    // Add header height to the default offset
    return `calc(${headerHeight}px + ${defaultOffset})`;
  }

  return defaultOffset;
};

/**
 * Check if position is on the right side
 */
export const isRightPosition = (position: NotificationPosition): boolean => {
  return position.includes('right');
};

/**
 * Check if position is on the left side
 */
export const isLeftPosition = (position: NotificationPosition): boolean => {
  return position.includes('left');
};

/**
 * Check if position is centered
 */
export const isCenterPosition = (position: NotificationPosition): boolean => {
  return position.includes('center');
};

/**
 * Check if position is at the top
 */
export const isTopPosition = (position: NotificationPosition): boolean => {
  return position.includes('top');
};

/**
 * Check if position is at the bottom
 */
export const isBottomPosition = (position: NotificationPosition): boolean => {
  return position.includes('bottom');
};
